import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Mail, MapPin, Phone, Plus, Minus } from "lucide-react";
import { FOOTER } from "@/constants/testIds";
import { CONTACT_INFO } from "@/data/anabiya";

const COL_SHOP = [
  { label: "All Products", to: "/shop" },
  { label: "Duvet Covers", to: "/shop?category=duvet-covers" },
  { label: "Sheet Sets", to: "/shop?category=sheet-sets" },
  { label: "Quilts", to: "/shop?category=quilts-bedspreads" },
  { label: "Curtains", to: "/shop?category=curtains" },
  { label: "Cushions", to: "/shop?category=decorative-cushions" },
  { label: "Home Decor", to: "/shop?category=home-decor" },
];

const COL_COMPANY = [
  { label: "About", to: "/about" },
  { label: "Our Story", to: "/about#story" },
  { label: "SPNE Design", to: "/spne-design" },
  { label: "Leadership", to: "/about#leadership" },
];

const COL_CARE = [
  { label: "Track Order", to: "/track-order" },
  { label: "Shipping", to: "/about#shipping" },
  { label: "Returns & Exchanges", to: "/about#returns" },
  { label: "FAQs", to: "/about#faqs" },
  { label: "Contact", to: `mailto:${CONTACT_INFO?.email || "hello@anabiyadecor.com"}` },
  { label: "WhatsApp Support", to: `https://wa.me/${CONTACT_INFO?.whatsapp || "919000000000"}`, external: true },
];

const COL_TRADE = [
  { label: "Wholesale Enquiry", to: "/about#trade" },
  { label: "Hotels & Resorts", to: "/about#trade" },
  { label: "Interior Designers", to: "/about#trade" },
  { label: "Request Quote", to: "/about#trade" },
];

const Column = ({ title, items, tid, collapsible = false }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="col-span-12 md:col-span-3 lg:col-span-2 border-b border-white/10 md:border-b-0 pb-3 md:pb-0" data-testid={`footer-col-${tid}`}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid={`footer-col-toggle-${tid}`}
          className="w-full flex items-center justify-between py-3 md:py-0 md:pointer-events-none"
        >
          <span className="overline text-gold tracking-[3px] text-[10px]">{title}</span>
          <span className="md:hidden text-gold">
            {open ? <Minus size={14} strokeWidth={1.6} /> : <Plus size={14} strokeWidth={1.6} />}
          </span>
        </button>
      ) : (
        <div className="overline text-gold tracking-[3px] text-[10px] mb-4">{title}</div>
      )}
      <ul className={`space-y-2.5 mt-1 md:mt-0 md:!block ${collapsible && !open ? "hidden" : "block"} md:mb-0 pb-3 md:pb-0`}>
        {items.map((item) => (
          <li key={item.label}>
            {item.external || item.to?.startsWith("http") || item.to?.startsWith("mailto:") ? (
              <a href={item.to} target={item.external ? "_blank" : undefined} rel="noopener noreferrer" className="text-white/70 hover:text-champagne transition-colors text-sm">
                {item.label}
              </a>
            ) : (
              <Link to={item.to} className="text-white/70 hover:text-champagne transition-colors text-sm">
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const Footer = () => {
  return (
    <footer data-testid={FOOTER.root} className="relative bg-forest text-white pt-20 md:pt-24 pb-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-12 gap-8 md:gap-10 pb-12 border-b border-white/10">
          {/* Brand column */}
          <div className="col-span-12 md:col-span-12 lg:col-span-4">
            <img
              src="/logo.png"
              alt="Anabiya Decor — Luxury Bedding & Home Textiles"
              width="240"
              height="90"
              className="h-16 md:h-20 w-auto object-contain bg-white rounded-sm p-2 shadow-[0_6px_24px_rgba(0,0,0,0.25)]"
              loading="lazy"
            />
            <p className="mt-6 max-w-sm text-white/70 leading-relaxed text-sm font-light">
              Anabiya Decor creates premium bedding, luxury linens and custom
              home textiles — crafted in Mumbai, shipped worldwide.
            </p>
            <div className="mt-6 space-y-1.5 text-sm text-white/60">
              {CONTACT_INFO?.address && (
                <div className="flex items-start gap-2">
                  <MapPin size={13} strokeWidth={1.4} className="text-gold mt-0.5 shrink-0" />
                  <span>{CONTACT_INFO.address}</span>
                </div>
              )}
              {CONTACT_INFO?.email && (
                <div className="flex items-center gap-2">
                  <Mail size={13} strokeWidth={1.4} className="text-gold shrink-0" />
                  <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-champagne">{CONTACT_INFO.email}</a>
                </div>
              )}
              {CONTACT_INFO?.phone && (
                <div className="flex items-center gap-2">
                  <Phone size={13} strokeWidth={1.4} className="text-gold shrink-0" />
                  <a href={`tel:${CONTACT_INFO.phone}`} className="hover:text-champagne">{CONTACT_INFO.phone}</a>
                </div>
              )}
            </div>
            <div className="mt-6 flex items-center gap-3">
              {CONTACT_INFO?.socials?.instagram && (
                <a href={CONTACT_INFO.socials.instagram} data-testid={FOOTER.social("instagram")} className="w-9 h-9 rounded-full border border-white/25 flex items-center justify-center hover:bg-champagne hover:text-forest transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <Instagram size={14} strokeWidth={1.4} />
                </a>
              )}
              {CONTACT_INFO?.socials?.facebook && (
                <a href={CONTACT_INFO.socials.facebook} data-testid={FOOTER.social("facebook")} className="w-9 h-9 rounded-full border border-white/25 flex items-center justify-center hover:bg-champagne hover:text-forest transition-colors" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <Facebook size={14} strokeWidth={1.4} />
                </a>
              )}
              {CONTACT_INFO?.socials?.linkedin && (
                <a href={CONTACT_INFO.socials.linkedin} data-testid={FOOTER.social("linkedin")} className="w-9 h-9 rounded-full border border-white/25 flex items-center justify-center hover:bg-champagne hover:text-forest transition-colors" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <Linkedin size={14} strokeWidth={1.4} />
                </a>
              )}
            </div>
          </div>

          {/* Link columns — SHOP + COMPANY collapse to accordion on mobile; CARE + TRADE stay expanded (always-visible info) */}
          <Column title="SHOP" items={COL_SHOP} tid="shop" collapsible />
          <Column title="COMPANY" items={COL_COMPANY} tid="company" collapsible />
          <Column title="CUSTOMER CARE" items={COL_CARE} tid="care" />
          <Column title="TRADE" items={COL_TRADE} tid="trade" />
        </div>

        <div className="pt-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-white/50 text-xs">
          <div>© {new Date().getFullYear()} Anabiya Decor · All rights reserved</div>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/about#privacy" className="hover:text-champagne">Privacy</Link>
            <Link to="/about#terms" className="hover:text-champagne">Terms</Link>
            <span>Designed & crafted in Mumbai</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
